import {useLayoutEffect, useRef, useCallback} from 'react';
import {Platform} from 'react-native';
import {useSelector} from 'react-redux';
import {selectScanPackSettings} from '../selectors/scanPackSelectors';

export const getKeyboardSuppressionProps = suppress => {
  if (!suppress) return {};
  return Platform.OS === 'web'
    ? {blurOnSubmit: false}
    : {showSoftInputOnFocus: false, blurOnSubmit: false};
};

export const applyWebInputMode = (node, suppress) => {
  if (
    Platform.OS !== 'web' ||
    !node ||
    typeof node.setAttribute !== 'function'
  ) {
    return;
  }
  const current = node.getAttribute('inputmode');
  if (suppress) {
    if (current !== 'none') node.setAttribute('inputmode', 'none');
  } else if (current === 'none') {
    node.removeAttribute('inputmode');
  }
};

/**
 * `inputmode="none"` hides the on-screen keyboard by telling the browser not to
 * open an input connection to the IME. On Android that also means printable
 * characters from an external HID scanner have nowhere to be committed: the key
 * events still reach the element (Enter submits, paste works) but no text is
 * ever inserted. So while suppression is on we insert the characters ourselves
 * from the keydown events that are already arriving.
 *
 * The write goes through the native value setter and a synthetic `input` event
 * so react-native-web's own onChange -> onChangeText fires exactly as it would
 * for real typing — no call site needs to know this is happening.
 */
const setNativeValue = (node, value) => {
  const proto =
    typeof window.HTMLTextAreaElement !== 'undefined' &&
    node instanceof window.HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(node, value);
  else node.value = value;
};

const handleSuppressedKeyDown = event => {
  const node = event.currentTarget;
  if (!node || node.readOnly || node.disabled) return;
  // keyCode 229 means the IME is still composing this key — not ours to handle.
  if (event.isComposing || event.keyCode === 229) return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  const isCharacter = event.key && event.key.length === 1;
  const isBackspace = event.key === 'Backspace';
  const isDelete = event.key === 'Delete';
  if (!isCharacter && !isBackspace && !isDelete) return;

  const value = node.value || '';
  const hasSelection =
    typeof node.selectionStart === 'number' &&
    typeof node.selectionEnd === 'number';
  let start = hasSelection ? node.selectionStart : value.length;
  let end = hasSelection ? node.selectionEnd : value.length;

  let insert = '';
  if (isCharacter) {
    insert = event.key;
  } else if (start !== end) {
    // A selection is deleted as-is by either key.
  } else if (isBackspace) {
    start = Math.max(0, start - 1);
  } else {
    end = Math.min(value.length, end + 1);
  }
  if (start === end && !insert) return;

  // We are the only writer while suppressed, so the result is identical on
  // browsers that would have inserted the character natively.
  event.preventDefault();

  const nextValue = value.slice(0, start) + insert + value.slice(end);
  const caret = start + insert.length;
  setNativeValue(node, nextValue);
  if (hasSelection) node.setSelectionRange(caret, caret);
  node.dispatchEvent(new Event('input', {bubbles: true}));
};

const useKeyboardSuppression = (inputRef, enabled = true) => {
  const scanPackSettings = useSelector(selectScanPackSettings);
  const suppress = enabled && !!scanPackSettings?.suppress_software_keyboard;
  const listeningNode = useRef(null);

  useLayoutEffect(() => {
    const node = inputRef?.current || null;
    applyWebInputMode(node, suppress);

    const target =
      Platform.OS === 'web' &&
      suppress &&
      node &&
      typeof node.addEventListener === 'function'
        ? node
        : null;
    if (listeningNode.current === target) return;
    if (listeningNode.current) {
      listeningNode.current.removeEventListener(
        'keydown',
        handleSuppressedKeyDown
      );
    }
    listeningNode.current = target;
    if (target) target.addEventListener('keydown', handleSuppressedKeyDown);
  });

  useLayoutEffect(
    () => () => {
      if (listeningNode.current) {
        listeningNode.current.removeEventListener(
          'keydown',
          handleSuppressedKeyDown
        );
        listeningNode.current = null;
      }
    },
    []
  );

  return suppress;
};

export const useSuppressedInputRef = (forwardedRef, enabled = true) => {
  const innerRef = useRef(null);
  const suppress = useKeyboardSuppression(innerRef, enabled);

  const setRef = useCallback(
    node => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  return {setRef, suppressionProps: getKeyboardSuppressionProps(suppress)};
};

export const KeyboardSuppressionSync = ({inputRef, enabled = true}) => {
  useKeyboardSuppression(inputRef, enabled);
  return null;
};

export default useKeyboardSuppression;
